import _ from 'lodash';
import client from './RestClient';
import {
  RtbCampaign,
  CheckPoint,
  VideoProgressTrackingCode,
  RTBCAMPAIGN_DEFAULT_AGE_MIN,
  RTBCAMPAIGN_DEFAULT_AGE_MAX,
  RtbLegacyOptimize,
  AdditionalInfoConversionTracking,
  TrackEvent,
  VideoAdViewObjective,
  VideoAdViewObjectiveType,
  VideoAdMetricEvent,
  RtbCampaignListBasic,
  RtbCampaignBasic,
  SpaceDevice,
  SpaceDeliverabilityData
} from 'core/rtbCampaign/RtbCampaign';
import { wrapLimitationsFromServer } from 'utils/LimitationUtil';
import { isAdTypeSupportOutdoorTA } from 'utils/CampaignUtil';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { LegacyEstimateData } from 'core/rtbCampaign/LegacyEstimateData';
import { AxiosInstance } from 'axios';
import { Dayparts } from 'core/dayparts/Dayparts';
import { TrackingData } from 'core/rtbCampaign/TrackingData';
import { ServerLimitation } from 'core/limitation/Limitation';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import { ScheduleData } from 'core/rtbCampaign/ScheduleData';
import { AdTypeOptionItem } from 'core/campaignDashboard/CampaignDashboard';
import { CookieUserDefaults } from 'utils/UserDefaults';
import { parseAggregatedHealthState } from 'enum/SpaceHealthState';
import fileDownload from 'js-file-download';

export type CreateCampaignRequest = {
  readonly campaign: any;
  readonly limitations: any;
};

export type UpdateCampaignRequest = {
  readonly campaign: any;
  readonly limitations: any;
};

export type LegacyEstimateDataPayLoad = {
  readonly dayPart?: Omit<Dayparts, 'enabled'>;
  readonly optimize: RtbLegacyOptimize;
  readonly orderId: number;
  readonly campaignId?: number;
  readonly bidPrice: number;
  readonly limitation: {
    op: string;
    limits: { value: string; label: string }[];
    type: string;
  }[];
};

const SPACE_DEVICE_I18N_FIELDS: Array<[string, string]> = [
  ['address', 'addressI18n']
];

function applyI18nFieldsToSpaceDevice(record: any, language: string): any {
  const result = { ...record };
  for (const [field, i18nField] of SPACE_DEVICE_I18N_FIELDS) {
    const i18nObj = result[i18nField];
    if (i18nObj && typeof i18nObj === 'object') {
      const localized = i18nObj[language.toLowerCase()] || i18nObj['en'];
      if (localized) result[field] = localized;
    }
    delete result[i18nField];
  }
  // ECOPD-5255: backend returns camelCase `healthStates` as an array of
  // UPPERCASE values；依聚合匹配優先順序歸一成單一 `healthState`（ON 先、
  // BANNED 最後）。`spaceStatusModifyReason` 與 `lastSyncedAt` 已同名，會經由
  // 上方 spread 帶入。
  result.healthState = parseAggregatedHealthState(result.healthStates);
  delete result.healthStates;
  return result;
}

export interface RtbCampaignWebService {
  getCampaign(campaignId: number): Promise<RtbCampaign>;
  getCampaignsOfGroup(
    l1ObjectId: number | string
  ): Promise<RtbCampaignListBasic[]>;
  createCampaign(body: any, l1ObjectId: number | string): Promise<void>;
  createCampaignAndCreative(
    body: any,
    l1ObjectId: number | string
  ): Promise<number>;
  createCampaignAndBindCreative(
    body: any,
    l1ObjectId: number | string
  ): Promise<number>;
  updateCampaign(body: any, l1ObjectId: number | string): Promise<void>;
  splitCampaign(
    body: any,
    campaignId,
    l1ObjectId: number | string
  ): Promise<void>;
  updateCampaignState(
    campaignData: {
      l2ChannelId: number | string;
      isDraft: boolean;
    }[],
    state: 'activate' | 'deactivate'
  ): Promise<void>;
  deleteCampaigns(campaignIds: Array<number>): Promise<void>;
  getCampaignOptions(from?: string, to?: string): Promise<Array<SelectOptions>>;
  getOrderNumber(campaignId: string | number): Promise<string>;
  getLegacyEstimateData(
    payload: LegacyEstimateDataPayLoad
  ): Promise<LegacyEstimateData>;
  updateCampaignBidWeight(
    campaignId: string | number,
    bidWeight: number
  ): Promise<void>;
  updateCampaignAdminBidPrice(
    campaignId: string | number,
    adminBidPrice?: number
  ): Promise<void>;
  getRtbCampaignsUsingDailyBudget(
    l1ObjectId: number | string
  ): Promise<RtbCampaignListBasic[]>;
  getAdTypeOptions(): Promise<SelectOptions[]>;
  getCampaignAdTypeOptions(): Promise<AdTypeOptionItem[]>;
  getOutdoorSpacesDetail(
    spaceIds: string[],
    testMode: boolean
  ): Promise<SpaceDevice[]>;
  approvePmp(body: any, pmpId: number): Promise<void>;
  rejectPmp(pmpId: number): Promise<void>;
  updateTrackingData(
    campaignIds: number[],
    trackingData: TrackingData
  ): Promise<void>;
  batchAddLimitations(
    campaignIds: number[],
    ageMin: number,
    ageMax: number,
    limitations: ServerLimitation[]
  ): Promise<void>;
  batchRemoveLimitations(
    campaignIds: number[],
    limitations: ServerLimitation[]
  ): Promise<void>;
  updateScheduleData(
    campaignIds: number[],
    scheduleData: Partial<ScheduleData>
  ): Promise<void>;
  downloadFixOutdoorDeliveryTargets(
    campaignId: number | undefined,
    name: string,
    channelGroupId: number,
    limitations: ServerLimitation[],
    deliveryUnit?: DeliveryUnit
  );
  getSpaceDeliverabilityData(
    groupId: number,
    adTypeId: number | null,
    isTesting: boolean,
    limitations: ServerLimitation[],
    campaignId?: number | null
  ): Promise<SpaceDeliverabilityData>;
}

function wrapCheckPoint(json: any): CheckPoint {
  return {
    target: _.get(json, 'target'),
    time: _.get(json, 'time'),
    utime: _.get(json, 'utime')
  };
}

function wrapTags(tags?: string | string[]): string[] {
  if (!tags) {
    return [];
  }
  if (Array.isArray(tags)) {
    return tags;
  }

  return tags.split(',');
}

const optimizeMap = {
  [RtbLegacyOptimize.CPC]: L2ObjectOptimizationGoal.CLICKS,
  [RtbLegacyOptimize.CPM]: L2ObjectOptimizationGoal.IMPRESSIONS,
  [RtbLegacyOptimize.CPV]: L2ObjectOptimizationGoal.VIDEO_VIEWS,
  [RtbLegacyOptimize.VCPM]: L2ObjectOptimizationGoal.VIDEO_VIEWS
};

function wrapBasic(json: any): RtbCampaignBasic {
  const orderPrice = _.get(json, 'orderPrice');
  let pmax3OptimizationGoal = optimizeMap[json.optimize] || json.optimize;
  if (json.optimize === RtbLegacyOptimize.CPM && !_.isEmpty(json.frequency)) {
    pmax3OptimizationGoal = L2ObjectOptimizationGoal.REACH;
  }
  // RKB-2712：PMP 活動的 adType 從後端來是空的，讓下游（廣告活動類型判斷、
  // 總覽頁的摘要分支選擇）都認不出它是 PMP。isPmp 才是可靠依據，這裡依它
  // 導出 adType，與活動列表既有的做法（CampaignListModel 的
  // `campaign.isPmp ? 'PMP' : campaign.adType`）一致。
  const isPmp = _.get(json, 'isPmp');
  return {
    id: _.get(json, 'campaignId', _.get(json, 'goganCampaignId')),
    name: _.get(json, 'name'),
    advertiserId: _.get(json, 'advertiserId'),
    orderId: _.get(json, 'orderId'),
    budget: _.get(json, 'budget', 0),
    startDate: _.get(json, 'startDate'),
    endDate: _.get(json, 'endDate'),
    createDate: _.get(json, 'createDate'),
    priceModel: _.get(json, 'priceModel'),
    orderPrice,
    optimize: pmax3OptimizationGoal
      ? pmax3OptimizationGoal
      : L2ObjectOptimizationGoal.UNSPECIFIED,
    bidPrice: _.get(json, 'bidPrice'),
    adminBidPrice: _.get(json, 'adminBidPrice'),
    creativeDeliverType: _.get(json, 'creativeDeliverType'),
    outdoorDeliverType: _.get(json, 'outdoorDeliverType'),
    deliverType: _.get(json, 'deliverType'),
    state: _.get(json, 'state'),
    spents: _.get(json, 'spents', _.get(json, 'spent', 0)),
    currencyRate: _.get(json, 'currencyRate'),
    olapActualSpent: _.get(json, 'olapActualSpent'),
    olapExpectSpent: _.get(json, 'olapExpectSpent'),
    expectedSpent: _.get(json, 'expectedSpent', 0),
    impres: _.get(json, 'impres', 0),
    clicks: _.get(json, 'clicks', 0),
    dailyTargetBudget: _.get(json, 'dailyTargetBudget'),
    frequency: _.get(json, 'frequency'),
    dayPart: _.get(json, 'dayPart'),
    checkpoints: _.get(json, 'checkpoints', []).map((json) =>
      wrapCheckPoint(json)
    ),
    enableMonitor: _.get(json, 'isMonitorEnable'),
    adType: isPmp ? 'PMP' : _.get(json, 'adType'),
    adTypeId: _.get(json, 'adTypeId'),
    videoAdViewObjective: wrapVideoAdViewObjectiveType(
      _.get(json, 'videoAdViewObjective')
    ),
    conversionTracking: _.get(json, 'conversionTracking'),
    viewTrackingCodes: _.get(json, 'viewTrackingCodes'),
    videoProgressTrackingCode: wrapVideoProgressTrackginCode(json),
    tags: wrapTags(_.get(json, 'tags')),
    convTrackEvent: _.get(json, 'convTrackEvent', TrackEvent.CLICK),
    orderPriceEnable:
      orderPrice === null || orderPrice === undefined ? false : true,
    goGanGroupId: _.get(json, 'goGanGroupId'),
    ageMin: _.get(json, 'ageMin'),
    ageMax: _.get(json, 'ageMax'),
    isDraft: _.get(json, 'draft', false),
    draftId: _.get(json, 'draftId'),
    isPmp,
    fixOutdoorSpace: _.get(json, 'fixOutdoorSpace'),
    isTestMode: _.get(json, 'isTestMode', false)
  };
}

// 匯出給測試直接呼叫：epaperActivatable 的預設值（null＝不適用）是個沒有畫面能反映的
// 契約決定，只能在這一層守（ECOPD-5619）。
export function wrapCampaignListBasic(json: any): RtbCampaignListBasic {
  const normalCampaignBasic = wrapBasic(json);
  return {
    ...normalCampaignBasic,
    additionalInfo: wrapAdditionalInfo(json),
    effectiveStatus: _.get(json, 'effectiveStatus'),
    l2ObjectId: _.get(json, 'l2ObjectId'),
    magnificationRatio: _.get(json, 'magnificationRatio', 1),
    report: _.get(json, 'report'),
    convs: _.get(json, 'convs', 0),
    adView: _.get(json, 'adView', 0),
    uuCount: _.get(json, 'uuCount'),
    viewable: _.get(json, 'viewable', 0),
    bindings: _.get(json, 'bindings'),
    adminBidPrice: _.get(json, 'adminBidPrice'),
    deliveryTargetCount: _.get(json, 'deliveryTargetCount', 0),
    deliveryTargetChangeCount: _.get(json, 'deliveryTargetChangeCount', 0),
    weatherFilteredCount: _.get(json, 'weatherFilteredCount', 0),
    simulcastMismatchCount: _.get(json, 'simulcastMismatchCount', 0),
    storeOverBindingLimitCount: _.get(json, 'storeOverBindingLimitCount', 0),
    spaceDeletedCount: _.get(json, 'spaceDeletedCount', 0),
    // 預設 null（＝這支沒回）而不是 []：/rtb-campaigns/usingDailyBudget 共用這支
    // 解包但不帶 additional_info，預設空陣列會讓那些活動被當成「有門市但一間都沒有」。
    deliveryTargetStatuses: _.get(json, 'deliveryTargetStatuses', null),
    // 預設 null（＝不適用）而不是 false：後端只在帶 additional_info=limitations 時
    // 才算這個欄位，缺值時當成「不能啟用」會把所有活動的啟用鍵鎖死。
    epaperActivatable: _.get(json, 'epaperActivatable', null)
  };
}

function wrapCreativeAmount(json: any) {
  return {
    bindingCount: _.get(json, 'bindingCount', 0),
    enableCount: _.get(json, 'enableCount', 0)
  };
}

function wrapAdditionalInfo(json: any) {
  const additionalInfo = _.get(json, 'additionalInfo', null);
  return {
    creativeAmount: wrapCreativeAmount(_.get(additionalInfo, 'creativeAmount')),
    limitations: wrapLimitationsFromServer(
      _.get(additionalInfo, 'limitations', []),
      json.dealIds,
      json.ageMin,
      json.ageMax,
      json.isTestMode,
      isAdTypeSupportOutdoorTA(json.adType, json.adTypeId)
    ),
    viewTrackingSize: _.get(additionalInfo, 'viewTrackingSize', 0),
    conversionTracking: wrapAdditionalConversionTracking(
      _.get(additionalInfo, 'conversionTracking')
    ),
    videoProgressTrackingOffset: _.get(
      additionalInfo,
      'videoProgressTrackingOffset'
    ),
    pmp: _.get(additionalInfo, 'pmp'),
    originPmp: _.get(additionalInfo, 'originPmp')
  };
}

function wrapAdditionalConversionTracking(
  json: any
): AdditionalInfoConversionTracking | undefined {
  if (!json) {
    return undefined;
  }

  return {
    conversionType: _.get(json, 'conversionType'),
    id: _.get(json, 'id'),
    name: _.get(json, 'name'),
    conversionId: _.get(json, 'conversionId')
  };
}

function wrapVideoProgressTrackginCode(
  json: any
): VideoProgressTrackingCode | undefined {
  const videoProgressTrackingCodeJsonValue = _.get(
    json,
    'videoProgressTrackingCode',
    null
  );
  if (videoProgressTrackingCodeJsonValue === null) {
    return undefined;
  }

  const videoProgressTrackingCodes: VideoProgressTrackingCode[] =
    videoProgressTrackingCodeJsonValue.map((json) => {
      return {
        offset: _.get(json, 'offset'),
        code: _.get(json, 'code')
      };
    });

  return videoProgressTrackingCodes[0];
}

function wrapVideoAdViewObjectiveType(json: any): VideoAdViewObjectiveType {
  const event = _.get(json, 'event');
  switch (event) {
    case VideoAdViewObjective.PROGRESS:
      return {
        videoAdEvent: VideoAdViewObjective.PROGRESS,
        offset: _.get(json, 'offset')
      };
    case VideoAdMetricEvent.FIRSTQUARTILE:
    case VideoAdMetricEvent.MIDPOINT:
    case VideoAdMetricEvent.COMPLETE:
      return {
        videoAdEvent: VideoAdViewObjective.METRIC,
        videoAdMetricEvent: event
      };
    default:
      return {
        videoAdEvent: VideoAdViewObjective.DEFAULT
      };
  }
}

export function wrapCampaign(json: any): RtbCampaign {
  const limitations = wrapLimitationsFromServer(
    _.get(json, 'limitations', []),
    _.get(json, 'campaign.dealIds'),
    _.get(json, 'campaign.ageMin', RTBCAMPAIGN_DEFAULT_AGE_MIN),
    _.get(json, 'campaign.ageMax', RTBCAMPAIGN_DEFAULT_AGE_MAX),
    _.get(json, 'campaign.isTestMode'),
    isAdTypeSupportOutdoorTA(
      _.get(json, 'campaign.adType'),
      _.get(json, 'campaign.adTypeId')
    )
  );
  return {
    basic: wrapBasic(_.get(json, 'campaign')),
    limitations
  };
}

function wrapCampaignList(json: any): RtbCampaignListBasic[] {
  return _.defaultTo(json, []).flatMap((json: any) =>
    wrapCampaignListBasic(json)
  );
}

function wrapLegacyEstimateData(json: any): LegacyEstimateData {
  return {
    impression: _.get(json, 'impression', 0),
    uniqueUser: _.get(json, 'uniqueUser', 0)
  };
}

export class RestfulCampaignWebService implements RtbCampaignWebService {
  restClient: AxiosInstance;

  constructor(
    restClient: AxiosInstance = client,
    private defualtFileDownload: any = fileDownload
  ) {
    this.restClient = restClient;
  }

  async getCampaign(campaignId: number): Promise<RtbCampaign> {
    const response = await this.restClient.get(
      `/v2/rtb-campaigns/${campaignId}`
    );
    return wrapCampaign(response.data);
  }

  async getCampaignsOfGroup(
    l1ObjectId: number | string
  ): Promise<RtbCampaignListBasic[]> {
    const response = await this.restClient.get(
      `/v3/l1-objects/${l1ObjectId}/campaigns`,
      {
        params: {
          additional_info:
            'creativeAmount,limitations,conversionTracking,viewTrackingSize,videoProgressTrackingOffset,pmp,originPmp'
        }
      }
    );
    return wrapCampaignList(response.data.records);
  }

  async createCampaign(body: any, l1ObjectId: number | string): Promise<void> {
    return this.restClient.post(`/v3/l1-objects/${l1ObjectId}/campaigns`, body);
  }

  async createCampaignAndCreative(
    body: any,
    l1ObjectId: number | string
  ): Promise<number> {
    const response = await this.restClient.post(
      `/v2/l1-objects/${l1ObjectId}/campaigns-with-creatives`,
      body
    );
    return _.get(response.data, 'campaign.campaignId', 0);
  }

  async createCampaignAndBindCreative(
    body: any,
    l1ObjectId: number | string
  ): Promise<number> {
    const response = await this.restClient.post(
      `/v2/l1-objects/${l1ObjectId}/campaigns-bind-creatives`,
      body
    );
    return _.get(response.data, 'campaign.campaignId', 0);
  }

  async updateCampaign(body: any, l1ObjectId: number | string): Promise<void> {
    let path = `/v3/l1-objects/${l1ObjectId}/campaigns/${_.get(body, 'campaign.campaignId')}`;
    return this.restClient.put(path, body);
  }

  async splitCampaign(
    body: any,
    campaignId,
    l1ObjectId: number | string
  ): Promise<void> {
    let path = `/v3/l1-objects/${l1ObjectId}/campaigns/${campaignId}/split`;
    return this.restClient.post(path, body);
  }

  async deleteCampaigns(campaignIds: number[]): Promise<void> {
    await this.restClient.delete(
      `/v2/rtb-campaigns?campaignIds=${campaignIds.join(',')}`
    );
  }

  async updateCampaignState(
    campaignData: {
      l2ChannelId: number | string;
      isDraft: boolean;
    }[],
    state: 'activate' | 'deactivate'
  ): Promise<void> {
    await this.restClient.put(`/v2/rtb-campaigns/${state}`, campaignData);
  }

  async getCampaignOptions(from?: string, to?: string) {
    const path = '/v2/rtb-campaigns/options';
    const params = { from, to };
    const paramsString = Object.keys(params)
      .filter((key) => params[key] !== undefined)
      .map((key) => `${key}=${params[key]}`)
      .join('&');
    const response = await this.restClient.get(`${path}?${paramsString}`);
    return _.get(response, 'data.records', []);
  }

  async getOrderNumber(campaignId: string | number): Promise<string> {
    const response = await this.restClient.get(
      `/v2/rtb-campaigns/${campaignId}/orderNumber`
    );
    return response.data;
  }

  async getLegacyEstimateData(
    payload: LegacyEstimateDataPayLoad
  ): Promise<LegacyEstimateData> {
    const response = await this.restClient.post(
      `/v2/rtb-campaigns/discovery`,
      payload
    );
    return wrapLegacyEstimateData(response.data);
  }

  async updateCampaignBidWeight(
    campaignId: string | number,
    bidWeight: number
  ): Promise<void> {
    return this.restClient.put(`/v2/rtb-campaigns/${campaignId}/bid-weight`, {
      bidWeight
    });
  }

  async updateCampaignAdminBidPrice(
    campaignId: string | number,
    adminBidPrice?: number
  ): Promise<void> {
    return this.restClient.put(
      `/v2/rtb-campaigns/${campaignId}/admin-bid-price`,
      { adminBidPrice: adminBidPrice || null }
    );
  }

  async getRtbCampaignsUsingDailyBudget(
    l1ObjectId: string | number
  ): Promise<RtbCampaignListBasic[]> {
    const response = await this.restClient.get(
      `/v2/l1-objects/${l1ObjectId}/rtb-campaigns/usingDailyBudget`
    );
    return wrapCampaignList(response.data);
  }

  async getAdTypeOptions(): Promise<SelectOptions[]> {
    const response = await this.restClient.get(
      '/v2/rtb-campaigns/ad-type/options'
    );
    return _.get(response, 'data.records', []);
  }

  async getCampaignAdTypeOptions(): Promise<AdTypeOptionItem[]> {
    const response = await this.restClient.get(
      '/v2/campaign-ad-types/options/campaign-dashboard'
    );
    return _.get(response, 'data.records', []);
  }

  async getOutdoorSpacesDetail(
    spaceIds: string[],
    testMode = false
  ): Promise<SpaceDevice[]> {
    const response = await this.restClient.post(
      `/v2/rtb-campaigns/outdoor-ad/spaces-detail?testMode=${testMode}`,
      spaceIds
    );
    const records = _.get(response, 'data.records', []) as any[];
    const language = new CookieUserDefaults().language;
    return records.map((record) =>
      applyI18nFieldsToSpaceDevice(record, language)
    );
  }

  async approvePmp(body: any, pmpId: number): Promise<void> {
    return this.restClient.put(`/pmp/${pmpId}/approval`, body);
  }

  async rejectPmp(pmpId: number): Promise<void> {
    return this.restClient.put(`/pmp/${pmpId}/reject`);
  }

  async updateTrackingData(
    campaignIds: number[],
    trackingData: TrackingData
  ): Promise<void> {
    return this.restClient.put('/v2/rtb-campaigns/tracking-codes/batch', {
      campaignIds,
      ...trackingData
    });
  }

  async batchAddLimitations(
    campaignIds: number[],
    ageMin: number,
    ageMax: number,
    limitations: ServerLimitation[]
  ): Promise<void> {
    return this.restClient.put('/v2/rtb-campaigns/limitations/batch', {
      campaignIds,
      ageMin,
      ageMax,
      limitations
    });
  }

  async batchRemoveLimitations(
    campaignIds: number[],
    limitations: ServerLimitation[]
  ): Promise<void> {
    return this.restClient.delete('/v2/rtb-campaigns/limitations/batch', {
      data: {
        campaignIds,
        limitations
      }
    });
  }

  async updateScheduleData(
    campaignIds: number[],
    scheduleData: Partial<ScheduleData>
  ): Promise<void> {
    return this.restClient.put('/v2/rtb-campaigns/dates/batch', {
      campaignIds,
      ...scheduleData
    });
  }

  async downloadFixOutdoorDeliveryTargets(
    campaignId: number | undefined,
    name: string,
    channelGroupId: number,
    limitations: ServerLimitation[],
    deliveryUnit?: DeliveryUnit
  ): Promise<void> {
    const response = await this.restClient.post(
      '/v2/rtb-campaigns/outdoor-delivery-targets-csv',
      {
        campaignId,
        channelGroupId,
        limitations,
        deliveryUnit
      }
    );
    this.defualtFileDownload(
      response.data,
      campaignId
        ? `${campaignId}_${name}_outdoor_delivery_units.csv`
        : `${name}_outdoor_delivery_units.csv`
    );
  }

  async getSpaceDeliverabilityData(
    groupId: number,
    // RKB-2712：PMP 活動沒有 adTypeId，一律送 null；
    // 後端收到 null 時視為單螢幕（不進同播分支）。
    adTypeId: number | null,
    isTesting: boolean,
    limitations: ServerLimitation[],
    // ECOPD-5621：編輯既有活動時要帶，後端才會把這檔活動自己從機台佔用數扣掉；
    // 不帶的話使用者一進編輯就會看到自己鎖著的機台被算成「超過鎖定上限」。
    campaignId?: number | null
  ): Promise<SpaceDeliverabilityData> {
    const response = await this.restClient.post(
      `/v2/rtb-campaigns/space-deliverability-preview`,
      {
        channelGroupId: groupId,
        adTypeId,
        testing: isTesting,
        limitations,
        campaignId: campaignId ?? null
      }
    );
    return response.data;
  }
}
